import { CircleAlert, CircleCheckBig, WandSparkles } from 'lucide-react';
import { useState } from 'react';
import { ApiError } from '../../api/envelope';
import type {
  CampaignAudience,
  CampaignChannel,
  CampaignReview,
  CampaignReviewArea,
} from '../../api/types';
import { useComms } from './context';

/**
 * What each kind of finding is called on screen. Named for what the operator
 * would go and fix rather than for where the finding came from: whether the
 * server settled it in code or a model judged it is our problem, not theirs.
 */
const REVIEW_AREA_LABELS: Record<CampaignReviewArea, string> = {
  promotions: 'Inbox placement',
  delivery: 'Carrier filtering',
  cost: 'Length & cost',
  personalisation: 'Personalisation',
  links: 'Links',
  tags: 'Merge tags',
  content: 'Content',
};

/**
 * A second pair of eyes on the copy, wherever the copy is about to reach people:
 * the builder's Review step, and a stopped campaign about to be resumed into the
 * half of the list that has not been mailed yet. One component because the thing
 * that makes it safe is not the markup — it is the staleness rule below, and a
 * second hand-written copy of this panel is exactly how that rule gets forgotten
 * on one of the two screens.
 *
 * It is asked for rather than run on arrival: it spends the tenant's model budget,
 * and a check that ran itself every time somebody stepped through would bill for
 * the times they were only passing through.
 */
export function CampaignReviewPanel({
  channel = 'email',
  subject = '',
  bodyHtml = '',
  bodyText,
  bodySms = '',
  audienceType,
  fromName,
  intro,
}: {
  /**
   * Which campaign this is. It decides what is read, what the findings are
   * about and every word on the panel — one component rather than two for the
   * reason the staleness rule below is one: a second hand-written copy is how
   * that rule gets forgotten on one of the screens.
   */
  channel?: CampaignChannel;
  subject?: string;
  bodyHtml?: string;
  /** Plain-text projection, when the caller has one — the server prefers it. */
  bodyText?: string;
  /** The text campaign's body — on that channel it is the whole copy. */
  bodySms?: string;
  audienceType?: CampaignAudience;
  fromName?: string;
  /** What this screen's reader is deciding, when it is not "before you send". */
  intro?: string;
}) {
  const isSms = channel === 'sms';
  /** What this panel is called and what it says it reads, per channel. */
  const noun = isSms ? 'message' : 'email';
  const { source } = useComms();
  const [review, setReview] = useState<CampaignReview | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * What the last review was OF. A verdict still on screen under copy that has
   * been edited since is the one way this feature could do harm: it would read as
   * clearance for a draft it never saw.
   *
   * State rather than a ref because it is compared during render — and adjusted
   * during render for the same reason, so a stale verdict does not survive even
   * the frame in which the words under it changed.
   */
  const signature = isSms ? bodySms : `${subject} ${bodyHtml}`;
  const [reviewedSignature, setReviewedSignature] = useState<string | null>(null);
  if (review && reviewedSignature !== signature) {
    setReviewedSignature(null);
    setReview(null);
    setError(null);
  }

  const empty = isSms ? !bodySms.trim() : !subject.trim() && !bodyHtml.trim();

  const run = async () => {
    setBusy(true);
    setError(null);
    setReview(null);
    try {
      const result = await source.api.reviewCampaign({
        channel,
        ...(isSms
          ? { bodySms: bodySms.trim() }
          : {
              subject: subject.trim(),
              bodyHtml,
              ...(bodyText?.trim() ? { bodyText: bodyText.trim() } : {}),
            }),
        ...(audienceType ? { audienceType } : {}),
        ...(fromName?.trim() ? { fromName: fromName.trim() } : {}),
      });
      setReviewedSignature(signature);
      setReview(result);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'The review could not be run — try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="cmg-review">
      <div className="cmg-review-head">
        <WandSparkles size={14} />
        <span>Check the {noun} before it goes</span>
        <button
          type="button"
          className="cbtn"
          style={{ marginLeft: 'auto' }}
          disabled={busy || empty}
          title={
            empty
              ? isSms
                ? 'Write the message first'
                : 'Write a subject and body first'
              : `Read the ${noun} and report what would hurt this send`
          }
          onClick={() => void run()}
        >
          {busy && <span className="btn-spin" />}
          {busy ? 'Reading…' : review ? 'Check again' : 'Review with AI'}
        </button>
      </div>
      {!review && !busy && !error && (
        <div className="cmg-hint">
          {intro ? `${intro} ` : ''}
          {isSms ? (
            <>
              Checks whether a carrier is likely to filter it, what it really costs in segments,
              whether a name or a price has been typed in where a merge tag belonged, and whether
              every link works.
            </>
          ) : (
            <>
              Checks whether it will land in Promotions rather than the inbox, whether a name or a
              date has been typed in where a merge tag belonged, and whether every link works.
            </>
          )}{' '}
          It reads the {noun} — it never changes it, and it never sends.
        </div>
      )}
      {error && <div className="comms-error-note">{error}</div>}
      {review && (
        <>
          <div className={`cmg-review-verdict ${review.verdict}`}>
            {review.verdict === 'ok' ? <CircleCheckBig size={15} /> : <CircleAlert size={15} />}
            <span>
              {review.summary ||
                (review.verdict === 'ok'
                  ? 'Nothing here would stop this going out.'
                  : 'Worth a look before you send.')}
            </span>
          </div>
          {review.findings.map((finding, index) => (
            <div
              // Findings have no id of their own and the list is replaced whole on
              // every run, so the index is stable for exactly as long as this
              // array is.
              key={`${finding.area}-${index}`}
              className={`cmg-finding ${finding.severity}`}
            >
              <span className="cmg-finding-area">{REVIEW_AREA_LABELS[finding.area]}</span>
              <div className="cmg-finding-main">
                <div className="cmg-finding-title">{finding.title}</div>
                <div className="cmg-finding-detail">{finding.detail}</div>
                {finding.evidence && <div className="cmg-finding-evidence">{finding.evidence}</div>}
              </div>
            </div>
          ))}
          {review.findings.length === 0 && (
            <div className="cmg-hint">
              {review.checked.links === 0
                ? 'No links to check.'
                : `All ${review.checked.links} link${review.checked.links === 1 ? '' : 's'} check out.`}{' '}
              {/* The measured cost belongs in the all-clear more than anywhere
                  else: on a clean text it is the one number the operator is
                  still deciding on, and the panel already has it in hand. */}
              {isSms && review.checked.segments
                ? `${review.checked.segments} segment${review.checked.segments === 1 ? '' : 's'} in ${review.checked.encoding}. `
                : ''}
              Nothing else stood out.
            </div>
          )}
          <div className="cmg-hint">A second opinion, not a gate — Send is yours either way.</div>
        </>
      )}
    </div>
  );
}
